import { DataSourceRegistryPath, pathToNameMap } from "features/data-source/data-source-registry";
import { UseDataSourceResult } from "features/data-source/data-source.types";
import { prepareNewApproachArgs } from "features/data-source/data-source.utils";
import { useDataSourceOldApproach } from "features/data-source/useDataSourceOldApproach";
import { useShouldUseNewApproach } from "features/data-source/useShouldUseNewApproach";
import { useGetDataSourceQuery } from "store/services/data-source.service";

/**
 * Temporary migration hook for fetching data sources using the new approach.
 *
 * This hook determines whether to use the new data source approach (by path)
 * or fall back to the old approach (by name), based on feature flags and
 * data source registry mappings.
 *
 * - If the "Refactor_DataSourcesNewApproach" feature flag is enabled and the
 *   data source path is registered for the new approach, the new query is used.
 * - Otherwise, the old approach is used as a fallback.
 *
 * This logic is only needed during the migration period and should be removed
 * once all data sources are migrated to the new approach.
 *
 * @param dataSourcePath The registry path of the data source to query.
 * @param parameters Optional parameters for the data source.
 * @param options Optional settings, e.g., skip.
 * @returns The result of the data source query.
 */
export const useDataSourceNewApproach = <T = unknown>(
    dataSourcePath: DataSourceRegistryPath,
    parameters?: Record<string, string | number | boolean>,
    options?: { skip?: boolean }
): UseDataSourceResult<T> => {
    const { useNewApproach } = useShouldUseNewApproach(dataSourcePath);

    const dataSourceName = pathToNameMap[dataSourcePath];
    const resultOld = useDataSourceOldApproach<T>(dataSourceName, parameters, {
        skip: options?.skip || useNewApproach,
    });

    const argsNew = prepareNewApproachArgs(dataSourcePath, parameters, options);
    const resultNew = useGetDataSourceQuery(argsNew, { skip: options?.skip || !useNewApproach });

    return useNewApproach ? resultNew : resultOld;
};
